import getExclusionReason from '@app.raytd.com/common/utils/tests/getExclusionReason';
import { toast } from 'sonner';
import { Assessment } from '../assessment/assessment.slice';
import { AssetEntity } from '../asset';
import { TestCase, TestItem, TestSuite } from '../tests/entity';
import type { ReferenceImage, ReferenceImageDisposition } from '../tests/api/referenceImages';
import { TestSpecificationsEntity } from '../tests/test-specifications.slice';
import { UsersEntity } from '../users';
import { transformClassificationTree } from './elementTransformer';
import {
  ActivityItem,
  AddCustomFieldOptionRequest,
  AddCustomFieldOptionResponse,
  ClassificationItem,
  DashboardRecentResponse,
  FastFillUpdateRequest,
  GetTestItemRequest,
  PublishedReportResponse,
  PublishReportPayload,
  PublishReportResponse,
  ReportBlock,
  ReportEntity,
  ReportPayload,
  ReportResponse,
  ReportRevision,
  ReportSection,
  ReportTestItem,
  ReportUpdatePayload,
  ToggleSwitchRequest,
  ToggleSwitchResponse,
  TestItemComment,
} from './types';
import updateItemInReportCache from './updateItemInReportCache';
import {
  NotificationPagination,
  AppNotification,
  ReportExport,
} from '../notifications/types';
import { rAPI } from './rAPI';

const reportSectionTags = (reportId?: number, sectionId?: string) =>
  [
    { type: 'ReportSections' as const, id: reportId },
    sectionId && { type: 'ReportSection' as const, id: sectionId },
  ].filter(Boolean);

export const publishReportInvalidatesTags = (
  result: PublishReportResponse | undefined,
  error: unknown,
  { body }: { id: number; body: PublishReportPayload }
) => {
  if (error || !result) return [];

  // Skip cache invalidation if this is a dry run
  if (body.dry_run) return [];

  return [
    { type: 'Report' as const, id: result.revision.report_id },
    { type: 'Reports' as const, id: 'LIST' },
    { type: 'ReportRevision' as const, id: result.revision.id.toString() },
    { type: 'ReportRevision' as const, id: 'LIST' },
  ];
};

export const inspectionsReportsAPI = rAPI.injectEndpoints({
  overrideExisting: false,
  endpoints: (builder) => ({
    getReportBlocks: builder.query<ReportBlock[], void>({
      query: () => 'report-blocks',
      transformResponse: (response: { data: ReportBlock[] }) => response.data,
      providesTags: ['ReportBlocks'],
    }),

    getReportBlock: builder.query<ReportBlock, string>({
      query: (id) => `report-blocks/${id}`,
      transformResponse: (response: { data: ReportBlock }) => response.data,
      providesTags: ['ReportBlocks'],
    }),

    createReportBlock: builder.mutation({
      query: (newReportBlock) => ({
        url: 'report-blocks',
        method: 'POST',
        body: newReportBlock,
      }),
      invalidatesTags: ['ReportBlocks'],
    }),

    updateReportBlock: builder.mutation({
      query: ({ id, ...block }) => ({
        url: `report-blocks/${id}`,
        method: 'PUT',
        body: block,
      }),
      invalidatesTags: ['ReportBlocks'],
    }),

    deleteReportBlock: builder.mutation<{ success: boolean }, number>({
      query: (id) => ({
        url: `report-blocks/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ReportBlocks'],
    }),

    archiveReportBlockForUser: builder.mutation({
      query: (id) => ({
        url: `report-blocks/${id}/status`,
        method: 'PUT',
      }),
      invalidatesTags: ['ReportBlocks'],
    }),

    archiveReportBlock: builder.mutation({
      query: (id) => ({
        url: `report-blocks/${id}/archive`,
        method: 'PUT',
      }),
      invalidatesTags: ['ReportBlocks'],
    }),

    duplicateReportBlock: builder.mutation({
      query: (id) => ({
        url: `report-blocks/${id}/duplicate`,
        method: 'POST',
      }),
      invalidatesTags: ['ReportBlocks'],
    }),

    getInspections: builder.query<Assessment[], void>({
      query: () => 'assessments2',
      providesTags: [{ type: 'Assessments', id: 'LIST' }],
    }),

    getReportInspections: builder.query<Assessment[], number>({
      query: (reportId) => `reports/${reportId}/assessments`,
      transformResponse: (response: { data: Assessment[] }) => response.data,
      providesTags: (result, error, reportId) => [
        { type: 'Assessment', id: reportId },
        'Assessment',
      ],
      keepUnusedDataFor: 60, // Cache lifetime in seconds
    }),

    getInspection: builder.query<Assessment, string>({
      query: (id) => `assessments/${id}`,
      transformResponse: (response: { data: Assessment }) => response.data,
      providesTags: (result, error, id) => [{ type: 'Assessment', id: id }],
    }),

    archiveInspection: builder.mutation({
      query: (assessmentId) => ({
        url: `assessments/${assessmentId}/archive`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, assessmentId) => [
        { type: 'Assessments', id: 'LIST' },
        { type: 'Assessment', id: assessmentId },
      ],
    }),

    archiveInspectionForUser: builder.mutation({
      query: (assessmentId) => ({
        url: `assessments/${assessmentId}/status`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, assessmentId) => [
        { type: 'Assessments', id: 'LIST' },
        { type: 'Assessment', id: assessmentId },
      ],
    }),

    updateInspection: builder.mutation({
      query: ({ id, ...inspection }) => ({
        url: `assessments/${id}`,
        method: 'PUT',
        body: inspection,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Assessments', id: 'LIST' },
        { type: 'Assessment', id: id },
      ],
    }),

    createInspection: builder.mutation({
      query: (inspection) => ({
        url: 'assessments',
        method: 'POST',
        body: inspection,
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Assessments', id: 'LIST' },
      ],
    }),

    getReports: builder.query<ReportEntity[], void>({
      query: () => 'reports',
      transformResponse: (response: { data: ReportEntity[] }) => response.data,
      providesTags: [{ type: 'Reports', id: 'LIST' }],
    }),

    getReport: builder.query<ReportEntity, string>({
      query: (id) => `reports/${id}`,
      transformResponse: (response: { data: ReportEntity }) => response.data,
      providesTags: (result) => {
        if (!result) return [{ type: 'Report', id: 'LIST' }];

        return [
          { type: 'Report', id: result.id },
          { type: 'Reports', id: 'LIST' },
          // Provide tags for sections
          ...result.sections.map((section) => ({
            type: 'ReportSection' as const,
            id: section.id,
          })),
          // Provide tags for test suites and their specifications
          ...result.testSuites
            .map((suite) => [
              { type: 'TestSuites' as const, id: suite.id },
              ...(suite.specifications?.map((spec) => ({
                type: 'Specifications' as const,
                id: spec.id,
              })) || []),
            ])
            .flat(),
        ];
      },
    }),

    getReportByUuid: builder.query<any, { uuid: string }>({
      query: ({ uuid }) => ({
        url: `report/view/${uuid}`,
      }),
      transformResponse: (response: {
        report: ReportEntity;
        revision: ReportRevision;
      }) => response,
    }),

    createReport: builder.mutation<ReportResponse, ReportPayload>({
      query: (payload) => {
        // const formData = new FormData();

        // if (payload['logo']) {
        //   formData.append('logo', payload['logo']);
        // }

        // const data = JSON.stringify({
        //   ...payload,
        //   logo: undefined,
        // });

        // formData.append('payload', data);

        return {
          url: 'reports',
          method: 'POST',
          body: payload,
        };
      },
      invalidatesTags: [{ type: 'Reports', id: 'LIST' }],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            inspectionsReportsAPI.util.updateQueryData('getReports', undefined, (draft) => {
              draft.push(data['data']);
            })
          );
        } catch {
          console.error('Error creating report');
        }
      },
    }),

    updateReport: builder.mutation<
      ReportEntity,
      Partial<ReportUpdatePayload> & Pick<ReportEntity, 'id'>
    >({
      query: ({ id, ...patch }) => ({
        url: `reports/${id}`,
        method: 'PUT',
        body: patch,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Report', id },
        { type: 'Reports', id: 'LIST' },
      ],
    }),

    deleteReport: builder.mutation<{ success: boolean; id: string }, string>({
      query: (id) => ({
        url: `reports/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Report', id },
        { type: 'Reports', id: 'LIST' },
      ],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          inspectionsReportsAPI.util.updateQueryData('getReports', undefined, (draft) => {
            const index = draft.findIndex((report) => report.id === id);
            if (index !== -1) draft.splice(index, 1);
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    archiveReport: builder.mutation({
      query: (id) => ({
        url: `reports/${id}/archive`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Report', id },
        { type: 'Reports', id: 'LIST' },
      ],
    }),

    archiveReportForUser: builder.mutation({
      query: (id) => ({
        url: `reports/${id}/status`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Report', id },
        { type: 'Reports', id: 'LIST' },
      ],
    }),

    getReportTestSuites: builder.query<TestSuite[], number>({
      query: (reportId) => `reports/${reportId}/test-suites`,
      transformResponse: (response: { data: TestSuite[] }) => response.data,
      providesTags: (result) => {
        if (!result) return [{ type: 'TestSuites', id: 'LIST' }];

        return [
          // Add tags for each test suite
          ...result.map((suite) => ({
            type: 'TestSuites' as const,
            id: suite.id,
          })),
          { type: 'TestSuites', id: 'LIST' },
          // Add tags for specifications in each test suite
          ...result.flatMap(
            (suite) =>
              suite.specifications?.map((spec) => ({
                type: 'Specifications' as const,
                id: spec.id,
              })) || []
          ),
          { type: 'Specifications', id: 'LIST' },
        ];
      },
    }),

    getReportItems: builder.query<ReportTestItem[], string>({
      query: (reportId) => `reports/${reportId}/test-items`,
      transformResponse: (response: { data: ReportTestItem[] }) =>
        response.data,
      providesTags: (result, error, reportId) =>
        result
          ? [
              ...result.map(({ assessmentTestId }) => ({
                type: 'TestItem' as const,
                id: assessmentTestId,
              })),
              { type: 'TestItems', id: `report-${reportId}` },
              { type: 'TestItems', id: 'LIST' },
            ]
          : [
              { type: 'TestItems', id: `report-${reportId}` },
              { type: 'TestItems', id: 'LIST' },
            ],
    }),

    getSingleReportItem: builder.query<
      ReportTestItem,
      { reportId: string; testItemId: number }
    >({
      query: ({ reportId, testItemId }) =>
        `reports/${reportId}/test-items/${testItemId}`,
      transformResponse: (response: { data: ReportTestItem }) => response.data,
      providesTags: (result) =>
        result
          ? [
              {
                type: 'TestItem' as const,
                id: result.assessmentTestId,
                reportId: result.reportId,
              },
            ]
          : [],
      async onQueryStarted(
        { reportId, testItemId },
        { dispatch, queryFulfilled, getState }
      ) {
        try {
          const { data: updatedItem } = await queryFulfilled;

          // Get current cache state
          const currentData = inspectionsReportsAPI.endpoints.getReportItems.select(reportId)(
            getState()
          )?.data;

          if (!currentData) return;

          const existingItem = currentData.find(
            (item) => item.assessmentTestId === testItemId
          );
          if (
            !existingItem ||
            JSON.stringify(existingItem) !== JSON.stringify(updatedItem)
          ) {
            // Create new array with updated item
            const updatedData = currentData.map((item) =>
              item.assessmentTestId === testItemId ? updatedItem : item
            );

            // Upsert the entire updated array
            dispatch(
              inspectionsReportsAPI.util.updateQueryData('getReportItems', reportId, (draft) => {
                // Find the item in the draft and replace it
                const itemIndex = draft.findIndex(
                  (item) => item.assessmentTestId === testItemId
                );
                if (itemIndex !== -1) {
                  draft[itemIndex] = updatedItem;
                }
              })
            );
          }
        } catch (error) {
          // Handle error if needed
          console.error('Failed to update report items cache:', error);
        }
      },
    }),

    getReportRevisions: builder.query<
      // PaginatedResponse<ReportRevision>,
      ReportRevision[],
      { report_id?: number; status?: string; page?: number; per_page?: number }
    >({
      query: (params) => ({
        url: `report/${params.report_id}/revisions`,
        params,
      }),
      transformResponse: (response: { data: ReportRevision[] }) =>
        response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: 'ReportRevision' as const,
                id,
              })),
              { type: 'ReportRevision', id: 'LIST' },
            ]
          : [{ type: 'ReportRevision', id: 'LIST' }],
    }),

    getReportRevision: builder.query<
      { report: ReportEntity; revision: ReportRevision },
      number
    >({
      query: (id) => `report/revisions/${id}`,
      transformResponse: (response: {
        report: ReportEntity;
        revision: ReportRevision;
      }) => response,
      providesTags: (result, error, id) => [{ type: 'ReportRevision', id }],
    }),

    createReportRevision: builder.mutation<
      ReportRevision,
      Pick<ReportRevision, 'report_id' | 'notes'>
    >({
      query: (body) => ({
        url: 'report/revisions',
        method: 'POST',
        body,
      }),
      transformResponse: (response: { data: ReportRevision }) => response.data,
      invalidatesTags: (result, error, body) => [
        { type: 'ReportRevision', id: 'LIST' },
        { type: 'Report', id: body.report_id },
        { type: 'Reports', id: 'LIST' },
      ],
    }),

    updateReportRevision: builder.mutation<
      ReportRevision,
      Pick<ReportRevision, 'id' | 'notes'>
    >({
      query: ({ id, ...patch }) => ({
        url: `report/revisions/${id}`,
        method: 'PUT',
        body: patch,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'ReportRevision', id },
        { type: 'ReportRevision', id: 'LIST' },
      ],
    }),

    deleteReportRevision: builder.mutation<void, number>({
      query: (id) => ({
        url: `report/revisions/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'ReportRevision', id },
        { type: 'ReportRevision', id: 'LIST' },
      ],
    }),

    getReportSections: builder.query<ReportSection[], number>({
      query: (reportId) => `reports/${reportId}/sections`,
      providesTags: (result, error, reportId) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: 'ReportSection' as const,
                id,
              })),
              { type: 'ReportSections' as const, id: reportId },
            ]
          : [{ type: 'ReportSections' as const, id: reportId }],
    }),

    getReportSection: builder.query<
      ReportSection,
      { reportId: number; sectionId: string }
    >({
      query: ({ reportId, sectionId }) =>
        `reports/${reportId}/sections/${sectionId}`,
      providesTags: (result, error, { reportId, sectionId }) =>
        reportSectionTags(reportId, sectionId),
    }),

    createReportSection: builder.mutation<
      ReportSection,
      { reportId: number; data: Partial<ReportSection> }
    >({
      query: ({ reportId, data }) => ({
        url: `reports/${reportId}/sections`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { reportId }) =>
        reportSectionTags(reportId),
    }),

    updateReportSection: builder.mutation<
      ReportSection,
      { reportId: number; sectionId: string; data: Partial<ReportSection> }
    >({
      query: ({ reportId, sectionId, data }) => ({
        url: `reports/${reportId}/sections/${sectionId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { reportId, sectionId }) =>
        reportSectionTags(reportId, sectionId),
      async onQueryStarted(
        { reportId, sectionId, data },
        { dispatch, queryFulfilled }
      ) {
        const patchResult = dispatch(
          inspectionsReportsAPI.util.updateQueryData('getReportSections', reportId, (draft) => {
            const section = draft.find((s) => s.id === sectionId);
            if (section) {
              Object.assign(section, data);
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    deleteReportSection: builder.mutation<
      void,
      { reportId: number; sectionId: string }
    >({
      query: ({ reportId, sectionId }) => ({
        url: `reports/${reportId}/sections/${sectionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { reportId, sectionId }) =>
        reportSectionTags(reportId, sectionId),
    }),

    upsertReportSections: builder.mutation<
      ReportSection[],
      {
        reportId: number;
        sections: Array<Partial<ReportSection>>;
      }
    >({
      transformResponse: (response: { data: ReportSection[] }) => response.data,
      query: ({ reportId, sections }) => ({
        url: `reports/${reportId}/sections`,
        method: 'POST',
        body: { sections },
      }),
      invalidatesTags: (result, error, { reportId }) => [
        { type: 'Report', id: reportId.toString() },
      ],
    }),

    getPublishedReport: builder.query<PublishedReportResponse, number>({
      query: (revisionId) => `report/revisions/${revisionId}/published`,
      transformResponse: (response: PublishedReportResponse) => response,
      async onQueryStarted(revisionId, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          // Update test suites cache with the published report's test suites
          if (data.published.report?.testSuites) {
            dispatch(
              inspectionsReportsAPI.util.upsertQueryData(
                'getReportTestSuites',
                Number(data.published.report?.id),
                data.published.report?.testSuites
              )
            );
          }
        } catch {
          // Handle error if needed
        }
      },
      providesTags: (result, error, revisionId) => [
        { type: 'ReportRevision', id: revisionId.toString() },
        { type: 'ReportRevision', id: 'LIST' },
      ],
    }),

    publishReport: builder.mutation<
      PublishReportResponse,
      { id: number; body: PublishReportPayload }
    >({
      query: ({ id, body }) => ({
        url: `report/${id}/publish`,
        method: 'POST',
        body,
      }),
      invalidatesTags: publishReportInvalidatesTags,
    }),
  }),
});

export const {
  useGetReportItemsQuery,
  useGetSingleReportItemQuery,
  useGetReportBlocksQuery,
  useGetReportBlockQuery,
  useCreateReportBlockMutation,
  useUpdateReportBlockMutation,
  useDeleteReportBlockMutation,
  useGetReportTestSuitesQuery,
  useArchiveReportBlockForUserMutation,
  useArchiveReportBlockMutation,
  useDuplicateReportBlockMutation,
  useGetInspectionsQuery,
  useArchiveInspectionMutation,
  useArchiveInspectionForUserMutation,
  useGetInspectionQuery,
  useUpdateInspectionMutation,
  useCreateInspectionMutation,
  useGetReportQuery,
  useGetReportsQuery,
  useCreateReportMutation,
  useUpdateReportMutation,
  useDeleteReportMutation,
  useArchiveReportForUserMutation,
  useArchiveReportMutation,
  useGetReportRevisionsQuery,
  useGetReportRevisionQuery,
  useCreateReportRevisionMutation,
  useUpdateReportRevisionMutation,
  useDeleteReportRevisionMutation,
  useGetReportSectionsQuery,
  useGetReportSectionQuery,
  useCreateReportSectionMutation,
  useUpdateReportSectionMutation,
  useDeleteReportSectionMutation,
  useUpsertReportSectionsMutation,
  useGetReportInspectionsQuery,
  useGetPublishedReportQuery,
  usePublishReportMutation,
  useGetReportByUuidQuery,
  useLazyGetReportByUuidQuery,
} = inspectionsReportsAPI;
